import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { activeMentionQuery, mentionParts, suggestMembers, type MentionSpan } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { hasRole, useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import { Button, cx, ErrorText } from './ui';

/** Discussion and following on the floor (INT-ANL-03, INT-FLR-02): threads, mentions, reactions, follows, read receipts. */

export type TargetType = 'ANALYSIS' | 'SETUP' | 'TRADE' | 'PROGRESS_REPORT' | 'CLOSING_REPORT';
export type FollowType = 'USER' | 'INSTRUMENT' | 'MARKET';
export type ReactionKind = 'AGREE' | 'DISAGREE' | 'LIKE';

export interface SocialSummary {
  comments: number;
  reactions: Record<ReactionKind, number>;
  mine: ReactionKind[];
  people: Partial<Record<ReactionKind, string[]>>;
}
export interface CommentView {
  id: string;
  parentId: string | null;
  author: { id: string; name: string };
  createdAt: string;
  editedAt: string | null;
  status: 'visible' | 'deleted' | 'hidden';
  body: string | null;
  mentions: MentionSpan[];
  canEdit: boolean;
  editableUntil: string | null;
  canDelete: boolean;
  canModerate: boolean;
  replies?: CommentView[];
}
interface Thread {
  target: { type: TargetType; id: string; label: string; link: string; discussable: boolean };
  count: number;
  comments: CommentView[];
}
export interface Member {
  id: string;
  name: string;
  roles: string[];
}
export interface FollowRow {
  targetType: FollowType;
  targetId: string;
  label: string;
  sub: string | null;
}

export const initials = (n: string) => n.split(/\s+/).map((p) => p[0]).join('').slice(0, 2).toUpperCase();

export function Avatar({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' }) {
  return (
    <div aria-hidden="true" className={cx('flex shrink-0 items-center justify-center rounded-full bg-[#2a2f3a] font-semibold', size === 'sm' ? 'h-7 w-7 text-[11px]' : 'h-8 w-8 text-xs')}>
      {initials(name)}
    </div>
  );
}

function ago(d: string, now: number, tz: string) {
  const m = Math.round((now - new Date(d).getTime()) / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  if (m < 24 * 60) return `${Math.floor(m / 60)}h ago`;
  return fmtTime(d, tz, true);
}

// ─────────────────────────────── data hooks ───────────────────────────────

export function useMembers() {
  return useQuery({ queryKey: ['mentionable-members'], queryFn: () => api<Member[]>('/mentions/members'), staleTime: 300_000 });
}

export function useFollows() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['social', 'follows'], queryFn: () => api<FollowRow[]>('/follows'), staleTime: 60_000 });
  const set = useMutation({
    mutationFn: ({ targetType, targetId, on }: { targetType: FollowType; targetId: string; on: boolean }) =>
      on ? api<FollowRow[]>('/follows', { body: { targetType, targetId } }) : api<FollowRow[]>(`/follows/${targetType}/${encodeURIComponent(targetId)}`, { method: 'DELETE' }),
    onSuccess: (rows) => {
      qc.setQueryData(['social', 'follows'], rows);
      void qc.invalidateQueries({ queryKey: ['feed'] });
    },
  });
  const isFollowing = (t: FollowType, id: string) => !!q.data?.some((f) => f.targetType === t && f.targetId === id);
  return { ...q, set, isFollowing };
}

/** Comment and reaction counts for several targets: keys from `targetKey(type, id)`. */
export function useSocialSummary(keys: string[], enabled = true) {
  return useQuery({
    queryKey: ['social', 'summary', ...keys],
    queryFn: () => api<Record<string, SocialSummary>>(`/social/summary?${keys.map((k) => `t=${encodeURIComponent(k)}`).join('&')}`),
    enabled: enabled && keys.length > 0,
  });
}

function useInvalidateSocial() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['social'] });
    void qc.invalidateQueries({ queryKey: ['feed'] });
  };
}

// ─────────────────────────────── follow ───────────────────────────────

const FOLLOW_NOUN: Record<FollowType, string> = { USER: 'trader', INSTRUMENT: 'symbol', MARKET: 'market' };

export function FollowButton({ targetType, targetId, label, className }: { targetType: FollowType; targetId: string; label: string; className?: string }) {
  const me = useMe();
  const f = useFollows();
  if (targetType === 'USER' && targetId === me.data?.id) return null;
  const on = f.isFollowing(targetType, targetId);
  return (
    <button
      type="button"
      title={on ? 'Select to unfollow' : undefined}
      disabled={f.set.isPending || f.isLoading}
      onClick={() => f.set.mutate({ targetType, targetId, on: !on })}
      className={cx('min-h-9 cursor-pointer rounded-full px-3.5 py-1.5 text-[13px] font-semibold disabled:opacity-60', on ? 'border border-[#1e5a3c] bg-up-bg text-up' : 'border border-[#2b4a78] bg-accent-bg text-accent', className)}
    >
      <span aria-hidden="true">{on ? '✓ ' : '+ '}</span>
      {on ? `Following ${label}` : `Follow ${label}`}
    </button>
  );
}

/**
 * A trader, symbol or market name that opens a small menu: follow / unfollow, and (on the floor) "show only this".
 * Keyboard: Enter/Space opens, arrows move, Escape closes.
 */
export function EntityMenu({ type, id, label, onFilter, className, children }: { type: FollowType; id: string; label: string; onFilter?: () => void; className?: string; children?: ReactNode }) {
  const me = useMe();
  const f = useFollows();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLSpanElement>(null);
  const menuId = useId();
  const self = type === 'USER' && id === me.data?.id;
  const on = f.isFollowing(type, id);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    box.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const onKey = (e: KeyboardEvent) => {
    const items = [...(box.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') {
      setOpen(false);
      box.current?.querySelector<HTMLElement>('button')?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(i + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(i - 1 + items.length) % items.length]?.focus();
    }
  };
  if (self && !onFilter) return <span className={className}>{children ?? label}</span>;

  return (
    <span ref={box} className="relative inline-flex" onKeyDown={onKey}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} onClick={() => setOpen((v) => !v)} className={cx('cursor-pointer rounded border-0 bg-transparent p-0 text-left text-inherit underline decoration-[#3a4250] decoration-dotted underline-offset-4 hover:decoration-accent', className)}>
        {children ?? label}
        {on && (
          <>
            <span aria-hidden="true" className="ml-1 text-[11px] font-normal text-up no-underline">
              ●
            </span>
            <span className="sr-only"> (following)</span>
          </>
        )}
      </button>
      {open && (
        <span id={menuId} role="menu" aria-label={label} className="absolute top-full left-0 z-30 mt-1.5 flex min-w-[220px] flex-col overflow-hidden rounded-xl border border-line2 bg-raised py-1 text-sm font-normal shadow-2xl">
          {!self && (
            <button
              type="button"
              role="menuitem"
              className="cursor-pointer px-3.5 py-2.5 text-left text-ink hover:bg-[#1c222b] focus:bg-[#1c222b] focus:outline-none"
              onClick={() => {
                f.set.mutate({ targetType: type, targetId: id, on: !on });
                setOpen(false);
              }}
            >
              {on ? `Unfollow ${label}` : `Follow ${label}`}
              <span className="block text-xs text-muted">{on ? `Stop alerts and the Following tab for this ${FOLLOW_NOUN[type]}` : `Alerts and the Following tab will include this ${FOLLOW_NOUN[type]}`}</span>
            </button>
          )}
          {onFilter && (
            <button
              type="button"
              role="menuitem"
              className="cursor-pointer px-3.5 py-2.5 text-left text-ink hover:bg-[#1c222b] focus:bg-[#1c222b] focus:outline-none"
              onClick={() => {
                onFilter();
                setOpen(false);
              }}
            >
              Show only {label}
            </button>
          )}
        </span>
      )}
    </span>
  );
}

// ─────────────────────────────── reactions ───────────────────────────────

function ThumbIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10v11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3Z" />
      <path d="M7 10l4-7a2.5 2.5 0 0 1 3 2.6L13.4 10H19a2 2 0 0 1 2 2.3l-1.3 7A2 2 0 0 1 17.7 21H7" />
    </svg>
  );
}
export function BubbleIcon() {
  return (
    <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1 1 21 12Z" />
    </svg>
  );
}

const REACTION_UI: Record<ReactionKind, { label: string; icon: ReactNode; on: string }> = {
  AGREE: { label: 'Agree', icon: <span aria-hidden="true">▲</span>, on: 'border-[#1e5a3c] bg-up-bg text-up' },
  DISAGREE: { label: 'Disagree', icon: <span aria-hidden="true">▼</span>, on: 'border-[#6a2a27] bg-down-bg text-down' },
  LIKE: { label: 'Like', icon: <ThumbIcon />, on: 'border-[#2b4a78] bg-accent-bg text-accent' },
};

/** Reaction buttons with counts. `readOnly` (e.g. your own post) shows counts without buttons. */
export function ReactionBar({ targetType, targetId, kinds, summary, readOnly, what }: { targetType: TargetType; targetId: string; kinds: ReactionKind[]; summary: SocialSummary | undefined; readOnly?: boolean; what?: string }) {
  const invalidate = useInvalidateSocial();
  const react = useMutation({ mutationFn: (kind: ReactionKind) => api<SocialSummary>('/reactions', { body: { targetType, targetId, kind } }), onSuccess: invalidate });
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={what ? `Reactions to ${what}` : 'Reactions'}>
      {kinds.map((k) => {
        const n = summary?.reactions[k] ?? 0;
        const mine = !!summary?.mine.includes(k);
        const ui = REACTION_UI[k];
        const who = summary?.people[k]?.join(', ');
        const name = `${ui.label}${what ? ` ${what}` : ''}: ${n} ${n === 1 ? 'person' : 'people'}`;
        if (readOnly)
          return (
            <span key={k} title={who} className="inline-flex min-h-8 items-center gap-1.5 rounded-full border border-line2 px-2.5 text-[13px] text-sub">
              <span aria-hidden="true" className="inline-flex items-center gap-1.5">
                {ui.icon} {k === 'LIKE' ? '' : ui.label} {n > 0 && <span className="font-mono">{n}</span>}
              </span>
              <span className="sr-only">{name}</span>
            </span>
          );
        return (
          <button
            key={k}
            type="button"
            aria-pressed={mine}
            aria-label={name}
            title={who}
            disabled={react.isPending || !summary}
            onClick={() => react.mutate(k)}
            className={cx('inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-[13px] font-semibold disabled:opacity-60', mine ? ui.on : 'border-line2 bg-panel text-sub hover:text-ink')}
          >
            {ui.icon} {k === 'LIKE' ? '' : ui.label} {n > 0 && <span className="font-mono">{n}</span>}
          </button>
        );
      })}
      {react.error && <span role="alert" className="text-xs text-down">{errorText(react.error)}</span>}
    </div>
  );
}

/** "💬 3 comments" toggle that opens a thread in place. */
export function DiscussToggle({ count, open, onClick, controls, label = 'Discuss' }: { count: number; open: boolean; onClick: () => void; controls?: string; label?: string }) {
  return (
    <button type="button" aria-expanded={open} aria-controls={controls} onClick={onClick} className={cx('inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-[13px] font-semibold', open ? 'border-[#2b4a78] bg-accent-bg text-accent' : 'border-line2 bg-panel text-sub hover:text-ink')}>
      <BubbleIcon /> {count ? `${count} ${count === 1 ? 'comment' : 'comments'}` : label}
    </button>
  );
}

// ─────────────────────────────── composer ───────────────────────────────

/** Comment box with @mention autocomplete (type "@" and a name; arrows + Enter or Tab to pick). Ctrl/⌘+Enter posts. */
export function MentionComposer({ onSubmit, label, placeholder, initial = '', submitLabel = 'Post', autoFocus, onCancel, busy }: { onSubmit: (text: string) => Promise<unknown>; label: string; placeholder?: string; initial?: string; submitLabel?: string; autoFocus?: boolean; onCancel?: () => void; busy?: boolean }) {
  const members = useMembers();
  const me = useMe();
  const [text, setText] = useState(initial);
  const [caret, setCaret] = useState(initial.length);
  const [active, setActive] = useState(0);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const pendingCaret = useRef<number | null>(null);
  const listId = useId();
  const hintId = useId();
  const q = activeMentionQuery(text, caret);
  const others = (members.data ?? []).filter((m) => m.id !== me.data?.id);
  // "@Ayesha Raza " (a full name followed by a space) is a finished mention: stop suggesting
  const finished = !!q && /\s$/.test(q.query) && others.some((m) => m.name.toLowerCase() === q.query.trim().toLowerCase());
  const list = q && !finished && dismissedAt !== q.start ? suggestMembers(others, q.query) : [];
  const open = list.length > 0;

  useEffect(() => setActive(0), [q?.query]);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const choose = (m: Member) => {
    if (!q) return;
    const insert = `@${m.name} `;
    const next = text.slice(0, q.start) + insert + text.slice(caret);
    const pos = q.start + insert.length;
    pendingCaret.current = pos;
    setText(next);
    setCaret(pos);
  };
  // Put the caret after the inserted mention in the same commit as the new text. Doing it a frame later let a
  // keystroke typed in between land after the old caret, so it ended up at the end of the comment.
  useLayoutEffect(() => {
    const pos = pendingCaret.current;
    if (pos === null || !ref.current) return;
    pendingCaret.current = null;
    ref.current.focus();
    ref.current.setSelectionRange(pos, pos);
  }, [text]);
  const submit = async () => {
    if (!text.trim() || busy) return;
    await onSubmit(text.trim());
    setText('');
    setCaret(0);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => (a + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        choose(list[active] ?? list[0]!);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setDismissedAt(q!.start);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit().catch(() => undefined);
    }
  };
  const sync = () => setCaret(ref.current?.selectionStart ?? text.length);

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <textarea
          ref={ref}
          rows={2}
          value={text}
          aria-label={label}
          aria-describedby={hintId}
          aria-autocomplete="list"
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open ? `${listId}-${active}` : undefined}
          placeholder={placeholder}
          onChange={(e) => {
            setText(e.target.value);
            setCaret(e.target.selectionStart ?? e.target.value.length);
            setDismissedAt(null);
          }}
          onKeyDown={onKeyDown}
          onKeyUp={(e) => {
            if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) sync();
          }}
          onClick={sync}
          onBlur={() => setTimeout(() => setDismissedAt(q?.start ?? null), 120)}
          onFocus={() => setDismissedAt(null)}
          className="w-full resize-y rounded-lg border border-line2 bg-field px-3 py-2.5 leading-relaxed text-ink placeholder:text-muted"
        />
        {open && (
          <ul id={listId} role="listbox" aria-label="Mention someone" className="absolute top-full right-0 left-0 z-30 mt-1 max-h-60 overflow-y-auto rounded-xl border border-line2 bg-raised py-1 shadow-2xl sm:right-auto sm:w-80">
            {list.map((m, i) => (
              <li
                key={m.id}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(m);
                }}
                onMouseEnter={() => setActive(i)}
                className={cx('flex cursor-pointer items-center gap-2.5 px-3 py-2', i === active && 'bg-accent-bg')}
              >
                <Avatar name={m.name} size="sm" />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate font-semibold">{m.name}</span>
                  <span className="text-xs text-muted">{m.roles.map((r) => r.replace('_', ' ').toLowerCase()).join(', ')}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span id={hintId} className="text-xs text-muted">
          Type @ to mention someone · Ctrl+Enter to post
        </span>
        <div className="flex gap-2">
          {onCancel && (
            <Button variant="ghost" className="min-h-9 py-1.5" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="primary" className="min-h-9 py-1.5" disabled={!text.trim() || busy} onClick={() => void submit().catch(() => undefined)}>
            {busy ? 'Posting…' : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────── thread ───────────────────────────────

function CommentText({ body, mentions, meId }: { body: string; mentions: MentionSpan[]; meId?: string }) {
  return (
    <p className="m-0 leading-relaxed break-words whitespace-pre-wrap text-ink2">
      {mentionParts(body, mentions).map((p, i) =>
        'mention' in p ? (
          <span key={i} className={cx('rounded px-0.5 font-semibold', p.mention.userId === meId ? 'bg-accent-bg text-accent' : 'text-accent')}>
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        ),
      )}
    </p>
  );
}

function CommentItem({ c, onReply, highlight, tz, now, reply }: { c: CommentView; onReply?: () => void; highlight?: string | null; tz: string; now: number; reply?: boolean }) {
  const me = useMe();
  const invalidate = useInvalidateSocial();
  const [mode, setMode] = useState<'view' | 'edit' | 'delete' | 'hide'>('view');
  const [reason, setReason] = useState('');
  const edit = useMutation({ mutationFn: (body: string) => api(`/comments/${c.id}`, { method: 'PATCH', body: { body } }), onSuccess: () => (setMode('view'), invalidate()) });
  const del = useMutation({ mutationFn: () => api(`/comments/${c.id}`, { method: 'DELETE' }), onSuccess: () => (setMode('view'), invalidate()) });
  const hide = useMutation({ mutationFn: (h: boolean) => api(`/comments/${c.id}/${h ? 'hide' : 'unhide'}`, { body: h ? { reason: reason || undefined } : {} }), onSuccess: () => (setMode('view'), invalidate()) });
  const err = edit.error ?? del.error ?? hide.error;
  const mine = c.author.id === me.data?.id;
  const link = 'cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-sub hover:text-ink';

  return (
    <article id={`comment-${c.id}`} aria-label={`Comment by ${c.author.name}`} className={cx('flex scroll-mt-24 gap-2.5 rounded-lg', highlight === c.id && 'bg-[#141d2b] ring-1 ring-[#2b4a78]', reply ? 'p-1.5' : 'p-1.5')}>
      <Avatar name={c.author.name} size="sm" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="font-semibold">{c.author.name}</span>
          <span className="text-xs text-muted">
            <time dateTime={c.createdAt} title={fmtTime(c.createdAt, tz, true)}>{ago(c.createdAt, now, tz)}</time>
            {c.editedAt && c.status !== 'deleted' ? ' · edited' : ''}
          </span>
          {c.status === 'hidden' && <span className="rounded-full bg-warn-bg px-2 text-[11px] font-semibold text-warn">Hidden{mine ? ' by a Master Trader' : ''}</span>}
        </div>
        {c.status === 'deleted' ? (
          <p className="m-0 text-sm text-muted italic">Comment deleted</p>
        ) : c.body === null ? (
          <p className="m-0 text-sm text-muted italic">Hidden by a Master Trader</p>
        ) : mode === 'edit' ? (
          <MentionComposer label="Edit your comment" initial={c.body} submitLabel="Save" autoFocus busy={edit.isPending} onSubmit={(b) => edit.mutateAsync(b)} onCancel={() => setMode('view')} />
        ) : (
          <CommentText body={c.body} mentions={c.mentions} meId={me.data?.id} />
        )}
        {mode === 'delete' && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-sub">Delete this comment? It will show as deleted.</span>
            <Button variant="danger" className="min-h-8 py-1" onClick={() => del.mutate()} disabled={del.isPending}>
              Delete
            </Button>
            <Button variant="ghost" className="min-h-8 py-1" onClick={() => setMode('view')}>
              Keep
            </Button>
          </div>
        )}
        {mode === 'hide' && (
          <div className="flex flex-wrap items-center gap-2">
            <input aria-label="Reason for hiding (optional)" placeholder="Reason (optional, kept in the audit log)" value={reason} onChange={(e) => setReason(e.target.value)} className="min-w-0 flex-1 rounded-lg border border-line2 bg-field px-3 py-1.5 text-sm text-ink placeholder:text-muted" />
            <Button variant="danger" className="min-h-8 py-1" onClick={() => hide.mutate(true)} disabled={hide.isPending}>
              Hide
            </Button>
            <Button variant="ghost" className="min-h-8 py-1" onClick={() => setMode('view')}>
              Cancel
            </Button>
          </div>
        )}
        {mode === 'view' && c.status !== 'deleted' && (
          <div className="flex flex-wrap gap-3.5">
            {onReply && c.status === 'visible' && (
              <button type="button" className={link} onClick={onReply} aria-label={`Reply to ${c.author.name}`}>
                Reply
              </button>
            )}
            {c.canEdit && (
              <button type="button" className={link} onClick={() => setMode('edit')} title={c.editableUntil ? `You can edit until ${fmtTime(c.editableUntil, tz)}` : undefined}>
                Edit
              </button>
            )}
            {c.canDelete && (
              <button type="button" className={link} onClick={() => setMode('delete')}>
                Delete
              </button>
            )}
            {c.canModerate && (
              <button type="button" className={cx(link, 'text-warn')} onClick={() => (c.status === 'hidden' ? hide.mutate(false) : setMode('hide'))}>
                {c.status === 'hidden' ? 'Show again' : 'Hide'}
              </button>
            )}
          </div>
        )}
        {err && <ErrorText error={errorText(err)} />}
      </div>
    </article>
  );
}

/** A full discussion for one item: comments, one level of replies, composer. Live via the "social.changed" socket event. */
export function CommentThread({ targetType, targetId, what, highlight, autoFocus }: { targetType: TargetType; targetId: string; what: string; highlight?: string | null; autoFocus?: boolean }) {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const invalidate = useInvalidateSocial();
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const { data, error, isLoading } = useQuery({ queryKey: ['social', 'comments', targetType, targetId], queryFn: () => api<Thread>(`/comments?targetType=${targetType}&targetId=${encodeURIComponent(targetId)}`) });
  const post = useMutation({ mutationFn: (v: { body: string; parentId?: string }) => api<CommentView>('/comments', { body: { targetType, targetId, ...v } }), onSuccess: () => (setReplyTo(null), invalidate()) });

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!highlight || !data) return;
    document.getElementById(`comment-${highlight}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlight, data]);

  if (error) return <ErrorText error={errorText(error)} />;
  if (isLoading || !data) return <p className="m-0 text-sm text-sub">Loading comments…</p>;
  const canWrite = hasRole(me.data?.roles, 'TRADER', 'MASTER_TRADER', 'RISK_MANAGER', 'ADMIN') && data.target.discussable;

  return (
    <section aria-label={`Comments on ${what}`} className="flex flex-col gap-2">
      {!data.comments.length && <p className="m-0 text-sm text-sub">No comments yet. Start the discussion.</p>}
      {data.comments.map((c) => (
        <div key={c.id} className="flex flex-col gap-1">
          <CommentItem c={c} tz={tz} now={now} highlight={highlight} onReply={canWrite ? () => setReplyTo(c.id) : undefined} />
          {(!!c.replies?.length || replyTo === c.id) && (
            <div className="ml-[22px] flex flex-col gap-1 border-l-2 border-line2 pl-3">
              {c.replies?.map((r) => <CommentItem key={r.id} c={r} tz={tz} now={now} highlight={highlight} reply onReply={canWrite ? () => setReplyTo(c.id) : undefined} />)}
              {replyTo === c.id && (
                <MentionComposer label={`Reply to ${c.author.name}`} placeholder={`Reply to ${c.author.name}…`} submitLabel="Reply" autoFocus busy={post.isPending} onSubmit={(body) => post.mutateAsync({ body, parentId: c.id })} onCancel={() => setReplyTo(null)} />
              )}
            </div>
          )}
        </div>
      ))}
      {post.error && <ErrorText error={errorText(post.error)} />}
      {canWrite && replyTo === null && <MentionComposer label={`Comment on ${what}`} placeholder="Add a comment… type @ to mention someone" busy={post.isPending} autoFocus={autoFocus} onSubmit={(body) => post.mutateAsync({ body })} />}
    </section>
  );
}

// ─────────────────────────────── read receipts ───────────────────────────────

/** "Read by 3 of 6" with the list of readers — rendered only for the author and Master Traders (the API enforces it). */
export function ReadReceipts({ analysisId, authorId }: { analysisId: string; authorId: string }) {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const { data, error } = useQuery({ queryKey: ['social', 'reads', analysisId], queryFn: () => api<{ count: number; audience: number; readers: { user: { id: string; name: string }; readAt: string }[] }>(`/read-receipts?targetType=ANALYSIS&targetId=${analysisId}`) });
  if (error || !data) return null;
  return (
    <details className="group relative">
      <summary className="flex min-h-9 cursor-pointer list-none items-center gap-2 rounded-full border border-line2 bg-panel px-3.5 text-[13px] font-semibold text-sub hover:text-ink">
        <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        Read by {data.count} of {data.audience}
      </summary>
      <div className="absolute right-0 z-20 mt-1.5 w-[min(300px,85vw)] rounded-xl border border-line2 bg-raised p-2 shadow-2xl">
        <p className="m-0 px-2 pt-1 pb-2 text-xs text-muted">{authorId === me.data?.id ? 'Only you and Master Traders can see this.' : 'Only the author and Master Traders can see this.'}</p>
        {!data.readers.length && <p className="m-0 px-2 pb-2 text-sm text-sub">No one has opened it yet.</p>}
        <ul className="m-0 flex list-none flex-col p-0">
          {data.readers.map((r) => (
            <li key={r.user.id} className="flex items-center justify-between gap-3 rounded-lg px-2 py-1.5">
              <span className="flex items-center gap-2">
                <Avatar name={r.user.name} size="sm" />
                {r.user.name}
              </span>
              <span className="text-xs text-muted">{fmtTime(r.readAt, tz, true)}</span>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

